/**
 * ============================================================
 * DIBAI 18K — Carrusel de Productos Premium
 * Asset: carrusel-prod-dibai.js
 * Versión: 1.0.0
 *
 * Funcionalidades:
 * - Carrusel drag/swipe/autoplay/loop
 * - Ajax Add to Cart (compatible Shopify OS 2.0)
 * - Barra de progreso dinámica
 * - Quick View modal con fetch
 * - Shopify Theme Editor events
 * ============================================================
 */

(function () {
  'use strict';

  // ─────────────────────────────────────────
  // CARRUSEL
  // ─────────────────────────────────────────
  class CarruselDibai {
    constructor(section) {
      this.section    = section;
      this.sectionId  = section.dataset.sectionId;
      this.carousel   = section.querySelector('.cpd-carousel');
      this.track      = section.querySelector('.cpd-track');
      this.slides     = Array.from(section.querySelectorAll('.cpd-slide'));
      this.prevBtn    = section.querySelector('[data-cpd-prev]');
      this.nextBtn    = section.querySelector('[data-cpd-next]');
      this.progressBar = section.querySelector('[data-cpd-progress]');

      this.count      = this.slides.length;
      this.current    = 0;
      this.offset     = 0;
      this._timer     = null;
      this._dragging  = false;
      this._startX    = 0;
      this._startOffset = 0;
      this._touchStartX = 0;
      this._touchStartY = 0;

      if (!this.track || this.count === 0) return;

      this.opt = {
        autoplay:   this.carousel.dataset.autoplay === 'true',
        speed:      parseInt(this.carousel.dataset.autoplaySpeed, 10) || 4000,
        loop:       this.carousel.dataset.loop === 'true',
        drag:       this.carousel.dataset.drag !== 'false',
        swipe:      this.carousel.dataset.swipe !== 'false',
      };

      this._cols      = 1;
      this._slideW    = 0;
      this._gap       = 0;
      this._maxOffset = 0;

      this._init();
    }

    _init() {
      this._measure();
      this._bindArrows();
      this._bindDrag();
      this._bindTouch();
      this._bindResize();
      this._updateProgress();
      this._updateArrows();
      if (this.opt.autoplay) this._startAutoplay();
    }

    // ── Medición de dimensiones ──
    _measure() {
      const style      = getComputedStyle(this.section);
      const w          = this.carousel.clientWidth;
      const gap        = parseFloat(style.getPropertyValue('--cpd-gap')) || 16;
      const vw         = window.innerWidth;
      const cols       = vw < 750
        ? parseFloat(style.getPropertyValue('--cpd-cols-mobile')) || 2
        : vw < 990
          ? parseFloat(style.getPropertyValue('--cpd-cols-tablet')) || 2
          : parseFloat(style.getPropertyValue('--cpd-cols-desktop')) || 4;

      this._cols      = Math.round(cols);
      this._gap       = gap;
      this._slideW    = (w - gap * (this._cols - 1)) / this._cols;
      this._stepW     = this._slideW + gap;
      this._maxOffset = Math.max(0, (this.count - this._cols) * this._stepW);

      // Actualizar offset al nuevo tamaño
      this.offset = Math.min(this.offset, this._maxOffset);
      this._applyOffset(false);
      this._updateProgress();
      this._updateArrows();
    }

    // ── Aplicar offset ──
    _applyOffset(animate = true) {
      if (!animate) this.track.classList.add('no-transition');
      this.track.style.transform = `translateX(-${this.offset}px)`;
      if (!animate) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.track.classList.remove('no-transition');
          });
        });
      }
    }

    // ── Navegar ──
    goTo(index) {
      if (this.opt.loop) {
        const total = this.count - this._cols;
        if (total <= 0) return;
        index = ((index % (total + 1)) + (total + 1)) % (total + 1);
      } else {
        index = Math.max(0, Math.min(index, this._maxOffset / this._stepW));
      }
      this.current = index;
      this.offset  = Math.min(index * this._stepW, this._maxOffset);
      this._applyOffset(true);
      this._updateProgress();
      this._updateArrows();
    }

    next() { this.goTo(this.current + 1); }
    prev() { this.goTo(this.current - 1); }

    // ── Barra de progreso ──
    _updateProgress() {
      if (!this.progressBar) return;
      const total  = this._maxOffset;
      const pct    = total > 0 ? (this.offset / total) * 100 : 0;
      this.progressBar.style.width = `${Math.min(100, Math.max(5, pct))}%`;
    }

    // ── Estado de flechas ──
    _updateArrows() {
      if (!this.prevBtn || !this.nextBtn) return;
      if (!this.opt.loop) {
        this.prevBtn.disabled = this.offset <= 0;
        this.nextBtn.disabled = this.offset >= this._maxOffset;
      } else {
        this.prevBtn.disabled = false;
        this.nextBtn.disabled = false;
      }
    }

    // ── Flechas ──
    _bindArrows() {
      this.prevBtn?.addEventListener('click', () => {
        this._stopAutoplay();
        this.prev();
        if (this.opt.autoplay) this._startAutoplay();
      });
      this.nextBtn?.addEventListener('click', () => {
        this._stopAutoplay();
        this.next();
        if (this.opt.autoplay) this._startAutoplay();
      });
    }

    // ── Mouse drag ──
    _bindDrag() {
      if (!this.opt.drag) return;
      this.carousel.addEventListener('mousedown', (e) => {
        this._dragging  = true;
        this._startX    = e.clientX;
        this._startOffset = this.offset;
        this.carousel.classList.add('is-dragging');
        this.track.classList.add('no-transition');
        this._stopAutoplay();
      });

      window.addEventListener('mousemove', (e) => {
        if (!this._dragging) return;
        const dx = this._startX - e.clientX;
        this.offset = Math.max(0, Math.min(this._maxOffset, this._startOffset + dx));
        this.track.style.transform = `translateX(-${this.offset}px)`;
      });

      window.addEventListener('mouseup', (e) => {
        if (!this._dragging) return;
        this._dragging = false;
        this.carousel.classList.remove('is-dragging');
        this.track.classList.remove('no-transition');
        // Snap al slide más cercano
        const snapped = Math.round(this.offset / this._stepW);
        this.goTo(snapped);
        if (this.opt.autoplay) this._startAutoplay();
      });
    }

    // ── Touch / Swipe ──
    _bindTouch() {
      if (!this.opt.swipe) return;
      this.carousel.addEventListener('touchstart', (e) => {
        this._touchStartX = e.touches[0].clientX;
        this._touchStartY = e.touches[0].clientY;
        this._startOffset = this.offset;
        this.track.classList.add('no-transition');
        this._stopAutoplay();
      }, { passive: true });

      this.carousel.addEventListener('touchmove', (e) => {
        const dx = this._touchStartX - e.touches[0].clientX;
        const dy = this._touchStartY - e.touches[0].clientY;
        if (Math.abs(dy) > Math.abs(dx)) return;
        this.offset = Math.max(0, Math.min(this._maxOffset, this._startOffset + dx));
        this.track.style.transform = `translateX(-${this.offset}px)`;
      }, { passive: true });

      this.carousel.addEventListener('touchend', (e) => {
        this.track.classList.remove('no-transition');
        const dx = this._touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(dx) > 40) {
          dx > 0 ? this.next() : this.prev();
        } else {
          // Snap al más cercano
          this.goTo(Math.round(this.offset / this._stepW));
        }
        if (this.opt.autoplay) this._startAutoplay();
      }, { passive: true });
    }

    // ── Resize ──
    _bindResize() {
      let _timer;
      window.addEventListener('resize', () => {
        clearTimeout(_timer);
        _timer = setTimeout(() => this._measure(), 200);
      });
    }

    // ── Autoplay ──
    _startAutoplay() {
      this._stopAutoplay();
      this._timer = setInterval(() => this.next(), this.opt.speed);
    }

    _stopAutoplay() {
      clearInterval(this._timer);
      this._timer = null;
    }

    destroy() {
      this._stopAutoplay();
    }
  }

  // ─────────────────────────────────────────
  // AJAX ADD TO CART
  // Horizon usa product-form-component (Web Component) que maneja
  // el carrito nativamente via @shopify/events. No necesitamos
  // interceptar el submit — el Web Component lo hace solo.
  // Solo añadimos feedback visual si el producto NO tiene
  // product-form-component (fallback para placeholders).
  // ─────────────────────────────────────────
  // ─────────────────────────────────────────
  // ATC: el product-form-component de Horizon maneja el submit.
  // Nosotros solo interceptamos para abrir el drawer via el evento
  // nativo 'shopify:cart:lines-update' que cart-drawer-component escucha.
  // ─────────────────────────────────────────
  function initAjaxCart(section) {
    section.addEventListener('submit', async (e) => {
      const form = e.target.closest('.cpd-card__form');
      if (!form) return;

      // Si tiene product-form-component: Horizon maneja el submit.
      // Solo nos encargamos de abrir el drawer al terminar.
      const hasNativeWC = !!e.target.closest('product-form-component');

      if (!hasNativeWC) {
        // Fallback para placeholders sin WC
        e.preventDefault();
        const btn = form.querySelector('button[type="submit"]');
        if (!btn || btn.disabled) return;
        btn.classList.add('is-loading');
        try {
          const formData = new FormData(form);
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          if (!res.ok) throw new Error();

          // Disparar el evento nativo que cart-drawer-component escucha
          // Event: 'shopify:cart:lines-update' con action:'add'
          // Esto hace que el drawer se abra automáticamente (auto-open)
          const cartEvent = new CustomEvent('shopify:cart:lines-update', {
            bubbles: true,
            detail: { action: 'add' }
          });
          // El objeto debe tener .action en el top level (no en detail)
          Object.defineProperty(cartEvent, 'action', { value: 'add' });
          document.dispatchEvent(cartEvent);

          // Fallback adicional: abrir el drawer directamente
          _openCartDrawer();

        } catch (err) {
          console.error('[CarruselDibai] ATC error:', err);
        } finally {
          form.querySelector('button[type="submit"]')?.classList.remove('is-loading');
        }
        return;
      }

      // Con product-form-component: escuchar cuando termine el add
      // y abrir el drawer si no se abrió solo
      const btn = form.querySelector('button[type="submit"]');
      if (!btn || btn.disabled) return;

      // Pequeño delay para que Horizon termine su proceso
      setTimeout(() => _openCartDrawer(), 800);
    });
  }

  /**
   * Abre el cart drawer de Horizon de forma directa.
   * Intenta múltiples métodos para máxima compatibilidad.
   */
  function _openCartDrawer() {
    // Método 1: usar el theme-drawer via su API pública
    const themeDrawer = document.querySelector('theme-drawer[id*="cart"], #cart-drawer theme-drawer, theme-drawer:has(cart-drawer-component)');
    if (themeDrawer && typeof themeDrawer.open === 'function') {
      themeDrawer.open();
      return;
    }

    // Método 2: simular clic en el botón del carrito del header
    const cartToggle = document.querySelector(
      '[aria-controls="cart-drawer"], [on\:click*="cart-drawer/toggle"], [data-testid="cart-drawer-trigger"]'
    );
    if (cartToggle) {
      // Solo abrir, no cerrar si ya está abierto
      const drawer = document.getElementById('cart-drawer');
      const isOpen = drawer?.hasAttribute('open') || drawer?.closest('theme-drawer')?.hasAttribute('open');
      if (!isOpen) cartToggle.click();
      return;
    }

    // Método 3: disparar el evento nativo de Shopify
    const ev = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
    Object.defineProperty(ev, 'action', { value: 'add', writable: false });
    Object.defineProperty(ev, 'promise', {
      value: Promise.resolve({ detail: { didError: false } }),
      writable: false
    });
    document.dispatchEvent(ev);
  }

  // ─────────────────────────────────────────
  // QUICK VIEW
  // ─────────────────────────────────────────
  const QV = {
    modal: null,
    body:  null,
    lastFocused: null,

    init() {
      this.modal = document.getElementById('cpd-quickview-modal');
      if (!this.modal) return;
      this.body = document.getElementById('cpd-quickview-body');

      // Cerrar con overlay y botón X
      this.modal.querySelectorAll('[data-cpd-qv-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      // ESC
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.modal.hidden) this.close();
      });
    },

    async open(productHandle, sectionUrl) {
      if (!this.modal) return;
      this.lastFocused = document.activeElement;

      this.body.innerHTML = '<div class="cpd-qv__loader"><div class="cpd-qv__spinner"></div></div>';
      this.modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';

      try {
        // Intentar cargar con la sección product-dibai-main (nuestra plantilla custom)
        const url = sectionUrl || `/products/${productHandle}?sections=product-dibai-main`;
        const resp = await fetch(url);
        const data = await resp.json();

        const sectionHtml = data['product-dibai-main'] || data[Object.keys(data)[0]];

        if (sectionHtml) {
          const wrapper = document.createElement('div');
          wrapper.className = 'cpd-qv__product';
          wrapper.innerHTML = sectionHtml;

          // Ejecutar scripts del contenido cargado
          this.body.innerHTML = '';
          this.body.appendChild(wrapper);

          // ── Ajustes de la vista rápida ──
          // Ocultar número NN/NN pero animar la barra al porcentaje correcto
          wrapper.querySelectorAll('*').forEach(el => {
            if (el.children.length === 0 && /^\s*\d+\/\d+\s*$/.test(el.textContent)) {
              el.style.display = 'none';
            }
          });
          // Animar barra de escasez en quickview
          wrapper.querySelectorAll('.ppv2-scarcity__fill').forEach(fill => {
            const pct = parseFloat(fill.dataset.pct || '90');
            // Remover el style inline que tiene width:0% para que CSS tome control
            fill.removeAttribute('style');
            // Agregar clase con el width final via CSS var
            fill.style.setProperty('--qv-bar-pct', pct + '%');
            fill.classList.add('qv-bar-animate');
          });
          // Inyectar CSS de animación si no existe
          if (!document.getElementById('qv-bar-style')) {
            const s = document.createElement('style');
            s.id = 'qv-bar-style';
            s.textContent = '.ppv2-scarcity__fill.qv-bar-animate { width: var(--qv-bar-pct, 90%) !important; transition: width 1s ease !important; }';
            document.head.appendChild(s);
          }

          // 1. Quitar descripción de colección
          wrapper.querySelectorAll('.ppv2-info__collection-desc').forEach(el => el.remove());

          // 2. Quitar "Pagando por CUALQUIER MEDIO DE PAGO"
          wrapper.querySelectorAll('.ppv2-promo-payment').forEach(el => el.remove());
          // Fallback: también buscar por texto por si la clase no aplica
          wrapper.querySelectorAll('p').forEach(p => {
            if (p.textContent.includes('Pagando por')) p.closest('div')?.remove();
          });

          // 3. Expandir acordeón de especificaciones
          wrapper.querySelectorAll('.ppv2-specs-accordion__body').forEach(body => {
            body.style.maxHeight = 'none';
            body.style.overflow  = 'visible';
            body.style.display   = 'block';
            body.style.opacity   = '1';
            body.style.visibility = 'visible';
          });
          wrapper.querySelectorAll('.ppv2-specs-accordion__trigger').forEach(btn => {
            btn.style.pointerEvents = 'none';
            btn.querySelector('.ppv2-specs-accordion__icon')?.remove();
          });

          // Re-ejecutar scripts inline para que Horizon inicialice sus WC
          wrapper.querySelectorAll('script').forEach((oldScript) => {
            const newScript = document.createElement('script');
            newScript.textContent = oldScript.textContent;
            if (oldScript.src) newScript.src = oldScript.src;
            oldScript.parentNode.replaceChild(newScript, oldScript);
          });

        } else {
          // Fallback: link a la página del producto
          this.body.innerHTML = `
            <div style="text-align:center;padding:48px 24px;font-family:'Montserrat',sans-serif;">
              <p style="margin-bottom:20px;color:#666;">Abre la página completa del producto para ver todos los detalles.</p>
              <a href="/products/${productHandle}" style="
                display:inline-block;padding:14px 32px;background:#111111;
                color:#fff;text-decoration:none;font-size:12px;
                letter-spacing:1.5px;text-transform:uppercase;font-weight:700;
              ">VER PRODUCTO COMPLETO</a>
            </div>
          `;
        }

        this.modal.querySelector('.cpd-qv__panel')?.focus();

      } catch (err) {
        console.error('[CarruselDibai] Quick View error:', err);
        this.body.innerHTML = `
          <div style="text-align:center;padding:48px 24px;">
            <p style="color:#666;margin-bottom:20px;">No se pudo cargar el producto.</p>
            <a href="/products/${productHandle}" style="
              display:inline-block;padding:14px 32px;background:#111;
              color:#fff;text-decoration:none;font-size:12px;
              letter-spacing:1.5px;text-transform:uppercase;font-weight:700;
            ">VER PRODUCTO</a>
          </div>
        `;
      }
    },

    close() {
      if (!this.modal) return;
      this.modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
      this.lastFocused?.focus();
    },
  };

  // ─────────────────────────────────────────
  // INICIALIZACIÓN GLOBAL
  // ─────────────────────────────────────────
  function initSection(sectionEl) {
    if (!sectionEl) return;

    // Carrusel
    if (!sectionEl._cpdCarousel) {
      sectionEl._cpdCarousel = new CarruselDibai(sectionEl);
    }

    // Ajax Cart
    initAjaxCart(sectionEl);

    // Quick View triggers (delegado desde el section)
    sectionEl.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cpd-quickview]');
      if (!btn) return;
      const handle     = btn.dataset.cpdQuickview;
      const sectionUrl = btn.dataset.productSectionUrl;
      if (handle) QV.open(handle, sectionUrl);
    });
  }

  function initAll() {
    // Quick View modal (singleton)
    QV.init();

    document.querySelectorAll('.cpd-section').forEach((el) => initSection(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // ─────────────────────────────────────────
  // SHOPIFY THEME EDITOR
  // ─────────────────────────────────────────
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector('.cpd-section');
    if (el) {
      el._cpdCarousel?.destroy();
      el._cpdCarousel = null;
      initSection(el);
      QV.init();
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target.querySelector('.cpd-section');
    el?._cpdCarousel?.destroy();
  });

})();
